import React from "react";
import classNames from "classnames";
import { createPortal } from "react-dom";
import { allocation, ResourceTypes } from "@planzone/dto";
import { ResourceAvatar } from "@common/resourceAvatar";
import { ActivityWatcher } from "@common/activityWatcher";
import { VisibilitySwitch } from "@common/visibilitySwitch";
import { UseCases } from "@planzone/core";
import { Safeguard } from "@scenes/safeguard";
import { AddAllocationIcon } from "@common/assets/addAllocationIcon";
import { LayoutWatcher } from "@common/layoutWatcher";
import { ScreenWatcher, Screen } from "@common/screenWatcher";
import { Context } from "../../context";
import { Props } from "./types";
import { Details } from "./details";
import styles from "./styles.scss";

function getAnchor(el: HTMLDivElement | null, screen: Screen) {
  if (el) {
    const rect = el.getBoundingClientRect();
    const horizontalOrientation =
      rect.x + 500 >= screen.width ? "left" : "right";
    const verticalOrientation = rect.top <= 550 ? "down" : "up";

    return {
      ...(horizontalOrientation === "left"
        ? {
            right: screen.width - rect.x - rect.width,
          }
        : {
            left: rect.x + rect.width + 10,
          }),
      ...(verticalOrientation === "up"
        ? {
            bottom: screen.height - rect.bottom + rect.height + 10,
          }
        : {
            top: rect.top,
          }),
    };
  }

  return { top: 0, left: 0 };
}

function AllocationsList({ allocations, project, task }: Props) {
  const { style = {} } = React.useContext(Context);
  const ref = React.useRef<HTMLDivElement | null>(null);

  return (
    <Safeguard useCases={[UseCases.allocateResources]} context={{ task }}>
      {({ canAllocateResources }) => (
        <div ref={ref} className={styles.allocationsList}>
          <VisibilitySwitch
            renderChildren={(isVisible, show, hide) => (
              <React.Fragment>
                <ul
                  className={styles.list}
                  onClick={(e) => {
                    if (canAllocateResources) {
                      e.stopPropagation();
                      (isVisible ? hide : show)();
                    }
                  }}
                >
                  <ActivityWatcher
                    blur={hide}
                    focus={show}
                    className={styles.main}
                  >
                    <React.Fragment>
                      {[...allocations]
                        .sort((a, b) =>
                          a.resource.name.localeCompare(b.resource.name),
                        )
                        .sort((a, b) => {
                          const priority = {
                            [ResourceTypes.HUMAN]: 1,
                            [ResourceTypes.VIRTUAL]: 2,
                            [ResourceTypes.MATERIAL]: 3,
                          };

                          return (
                            priority[a.resource.type] -
                            priority[b.resource.type]
                          );
                        })
                        .slice(0, 2)
                        .map(({ resource }) => (
                          <li
                            key={resource.uuid}
                            className={styles.allocation}
                            style={
                              !!style.itemHeight
                                ? {
                                    minHeight: style.itemHeight * 0.7,
                                    minWidth: style.itemHeight * 0.7,
                                    height: 0,
                                    width: 0,
                                  }
                                : undefined
                            }
                          >
                            <ResourceAvatar resource={resource} />
                            <div className={styles.infoBubble}>
                              <p>{resource.name}</p>
                            </div>
                          </li>
                        ))}
                      {allocations.slice(2).length > 0 && (
                        <li
                          className={classNames(
                            styles.allocation,
                            styles.summary,
                          )}
                          style={
                            !!style.itemHeight
                              ? {
                                  minHeight: style.itemHeight * 0.7,
                                  minWidth: style.itemHeight * 0.7,
                                  height: 0,
                                  width: 0,
                                }
                              : undefined
                          }
                        >
                          <p>+{allocations.slice(2).length}</p>
                          <div className={styles.infoBubble}>
                            {allocations.slice(2).map(({ resource }, key) => (
                              <p key={key}>{resource.name}</p>
                            ))}
                          </div>
                        </li>
                      )}
                      {canAllocateResources && allocations.length === 0 && (
                        <li className={styles.allocation}>
                          <div className={styles.menu}>
                            <div>
                              <AddAllocationIcon
                                style={
                                  !!style.itemHeight
                                    ? {
                                        minHeight: style.itemHeight * 0.7,
                                        minWidth: style.itemHeight * 0.7,
                                        height: 0,
                                        width: 0,
                                      }
                                    : undefined
                                }
                                className={styles.addAllocationIcon}
                              />
                            </div>
                          </div>
                        </li>
                      )}
                    </React.Fragment>
                  </ActivityWatcher>
                </ul>
                {isVisible && ref.current && (
                  <LayoutWatcher
                    renderChildren={({ top }) => (
                      <ScreenWatcher
                        renderChildren={(screen) => (
                          <div
                            className={classNames(styles.resourcesList, {
                              [styles.up]: 310 + top >= screen.height,
                              [styles.down]: top <= 550,
                            })}
                          >
                            <div
                              className={styles.overlay}
                              onClick={(e) => {
                                e.stopPropagation();
                                hide();
                              }}
                            />
                            {createPortal(
                              <div
                                className={styles.details}
                                style={getAnchor(ref.current, screen)}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className={styles.detailsWrapper}>
                                  <Details
                                    allocations={allocations}
                                    project={project}
                                    task={task}
                                  />
                                </div>
                              </div>,
                              document.body,
                            )}
                          </div>
                        )}
                      />
                    )}
                  />
                )}
              </React.Fragment>
            )}
          />
        </div>
      )}
    </Safeguard>
  );
}

export { AllocationsList };
